<template>
  <div class="dm-cp">
    <el-alert class="dm-cp__alert" type="info" :closable="false" show-icon :title="$t(headline)">
      <div class="dm-cp__base">
        <template v-if="hasMenuPrice"
          >{{ $t('메뉴 단가') }}: <strong>{{ money(menuPrice) }}</strong></template
        >
        <template v-else>{{ $t('이 메뉴는 싯가라 배달 목표가 없습니다.') }}</template>
      </div>
    </el-alert>

    <el-table :data="rows" border size="mini" :empty-text="$t('연결된 배달앱이 없습니다.')">
      <el-table-column :label="$t('플랫폼')" width="100">
        <template slot-scope="s">{{ platformName(s.row.platform) }}</template>
      </el-table-column>

      <!--
        **한 플랫폼에 링크가 여럿일 수 있다** — 한 샵에 판이 둘이거나 매장이 같은 플랫폼에 샵을
        여럿 가진 경우다. 편집 축은 `(플랫폼, 채널)` 이라(같은 축을 두 번 실으면 400) 여기서 접고,
        접힌 값이 갈리면 그 사실을 드러낸다.
      -->
      <el-table-column :label="$t('연결')" min-width="200">
        <template slot-scope="s">
          <div v-for="link in s.row.links" :key="link.linkId" class="dm-cp__link">
            <span>{{ shop(link) }}</span>
            <span v-if="link.menuBoardRef" class="dm-cp__weak"> · {{ link.menuBoardRef }}</span>
            <span class="dm-cp__effective">
              {{ $t('배달') }} {{ money(link.delivery) }}
              <template v-if="hasPickup(s.row.platform)"> · {{ $t('포장') }} {{ money(link.pickup) }}</template>
            </span>
          </div>
        </template>
      </el-table-column>

      <el-table-column :label="$t('배달 목표')" width="200">
        <template slot-scope="s">
          <el-input-number
            v-model="drafts[s.row.platform][CHANNEL.DELIVERY].value"
            size="mini"
            controls-position="right"
            :min="0"
            :max="90000000"
            :placeholder="$t('메뉴 단가 사용')"
            class="dm-cp__input dm-cp__input--delivery"
            @change="touch(s.row.platform, CHANNEL.DELIVERY)"></el-input-number>
          <div v-if="s.row.channels[CHANNEL.DELIVERY].mixed" class="dm-cp__mixed">
            {{ $t('연결마다 값이 다릅니다 — 입력하면 모두 같은 값이 됩니다') }}
          </div>
        </template>
      </el-table-column>

      <!--
        **요기요·쿠팡에는 포장 축 자체가 없다** (§16.171·§16.177). 두 플랫폼은 포장가 필드를
        갖지 않아 관측하지 않으므로 값을 넣어도 어디에도 비교되지 않는다 — 서버는 플랫폼으로
        분기하지 않으니 입력칸을 여는 판단이 화면 몫이다. 어느 플랫폼에 열리는지는 플랫폼 표가
        답한다(`platformHasPickupChannel` — 배민·땡겨요).
      -->
      <el-table-column :label="$t('포장 목표')" width="200">
        <template slot-scope="s">
          <template v-if="s.row.channels[CHANNEL.PICKUP]">
            <el-input-number
              v-model="drafts[s.row.platform][CHANNEL.PICKUP].value"
              size="mini"
              controls-position="right"
              :min="0"
              :max="90000000"
              :placeholder="$t('미설정 — 반영 안 함')"
              class="dm-cp__input dm-cp__input--pickup"
              @change="touch(s.row.platform, CHANNEL.PICKUP)"></el-input-number>
            <div v-if="s.row.channels[CHANNEL.PICKUP].mixed" class="dm-cp__mixed">
              {{ $t('연결마다 값이 다릅니다 — 입력하면 모두 같은 값이 됩니다') }}
            </div>
          </template>
          <span v-else class="dm-cp__weak">{{ $t('이 배달앱에는 포장 가격이 없습니다') }}</span>
        </template>
      </el-table-column>
    </el-table>

    <!--
      **"저장되지 않았다"가 아니라 "이 축은 반영하지 않는다"이다** (§16.177). 이 구분을 하지 않으면
      사장님은 값이 저장되지 않은 줄 알고 같은 입력을 반복한다.

      경고는 **포장 축에만** 붙는다(`pickupPendingNote` — 포장 입력칸이 열린 플랫폼이 없으면 `null`). 배달 축은
      목표가 그대로 나가므로 거기에 부기를 남기면 반영된 값을 반영되지 않았다고 읽게 된다.

      포장 축의 부기는 "안 된다"가 아니라 **조건**이고, 조건은 **플랫폼마다 다르다** — 배민은 `매장가 ≥ 포장가`
      를 강제해 매장가격을 세워 둔 메뉴에서만 나가고(2026-08-20 개통), 땡겨요는 가게의 포장 서비스가 켜져 있을
      때만 나간다(§16.307 — 꺼져 있으면 룰이 그 축만 건너뛴다). 실패는 화면에 `해당 변경 미지원` 으로만
      도착하므로(사유는 서버가 담지 않는다) **값을 정하는 이 자리에서** 이 표에 있는 플랫폼의 조건만 말한다.
    -->
    <el-alert
      v-if="pickupPendingNote"
      class="dm-cp__alert"
      type="warning"
      :closable="false"
      show-icon
      :title="pickupPendingNote"></el-alert>
    <div class="dm-cp__foot">
      {{ $t('배달앱에 실제로 반영됐는지는 메뉴 목록의 배달 연동 상태에서 확인할 수 있습니다.') }}
    </div>
  </div>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import { util } from '@/mixins/utilMixin'
import {
  channelPriceRows,
  channelPriceTargets,
  DELIVERY_MENU_API,
  deliveryMenuPlatformName,
  platformHasPickupChannel,
  shopText,
} from '@/utils/deliveryMenuSync'
import { DELIVERY_MENU_CHANNEL, deliveryChannelPendingNote } from '@/utils/deliveryMenuNotes'
/**
 * 메뉴 수정 — **채널별 목표 가격** (`PUT /menus/{smenuCd}/channel-prices`, §16.177).
 *
 * **사장님이 목표를 *정하는* 자리다.** ④ 드리프트의 `ACCEPT_PLATFORM` 은 *"플랫폼에 이미 있는 값을
 * 받아들이겠다"* 이지 *"배민 포장가는 3,000원으로 하겠다"* 가 아니라, 이 화면이 생기기 전에는 사장님이
 * 배민 포장가를 오더엔에서 정할 자리가 없었다.
 *
 * ## 이 컴포넌트가 지키는 것 셋
 *
 * 1. **오버라이드와 실효값은 다른 사실이다.** 입력칸은 그 링크가 *소유한* 목표(`deliveryOverride`)를
 *    보여주고 비어 있으면 "메뉴 단가를 따른다"이며, 옆의 실효값(`delivery`)이 실제로 나갈 값이다.
 *    `effective` 만 쓰면 "메뉴 단가와 같은 값"과 "명시적으로 그 값을 정했다"를 구분할 수 없다.
 * 2. **손댄 축만 보낸다.** 목록에 없는 채널을 서버는 건드리지 않는다 — 안 만진 축까지 실으면 두
 *    링크가 정당하게 갈라 놓은 값이 조용히 하나로 합쳐진다.
 * 3. **저장 응답을 그대로 되비춘다.** 저장 규칙은 항등이 아니다 — 배달 채널에 메뉴 단가와 같은 값을
 *    보내면 오버라이드는 `null` 로 접힌다. 자기가 보낸 값을 믿으면 다음 조회에서 값이 사라져 보인다.
 *
 * 조회는 **부모가 한다** — 링크가 0건이면 이 영역 자체가 없어야 하는데, 그 사실은 응답이 답하기
 * 때문이다(자기가 붙어 있어야 조회할 수 있는 구조면 탭이 떴다 사라진다).
 */
export default {
  name: 'DeliveryMenuChannelPricePanel',
  mixins: [mainVueMixin],

  props: {
    /** `GET /menus/{smenuCd}/channel-prices` 응답 — 부모가 읽어 넘긴다 */
    data: { type: Object, default: null },
  },

  data() {
    return {
      CHANNEL: DELIVERY_MENU_CHANNEL,
      /** `{[platform]: {[channel]: {value, touched}}}` — 편집 상태 */
      drafts: {},
    }
  },

  computed: {
    rows() {
      return channelPriceRows(this.data)
    },
    /**
     * ★ **싯가 메뉴는 배달 축에도 목표가 없다** (§16.226). 서버가 그 사실을 `menuPrice: null` 로
     * 내려주므로(포장 미설정과 **같은 어휘**다) 화면은 새 분기를 만들지 않고 그 값을 읽는다.
     * 그 상태에서 「비워 두면 단가를 그대로 씁니다」는 거짓이다 — planner 가 그 축의 목표를
     * 아예 만들지 않아 배달앱에는 옛 값이 남는다.
     */
    hasMenuPrice() {
      return this.menuPrice !== null && this.menuPrice !== undefined
    },
    headline() {
      return this.hasMenuPrice
        ? '배달앱에 내보낼 목표 가격입니다. 배달 가격을 비워 두면 이 메뉴의 단가를 그대로 씁니다 — 그것이 기본입니다. 포장 가격은 다릅니다: 비워 두면 목표가 없어 반영하지 않습니다.'
        : '배달앱에 내보낼 목표 가격입니다. 이 메뉴는 싯가라 배달·포장 모두 비워 두면 목표가 없어 반영하지 않습니다 — 값을 넣은 채널만 나갑니다.'
    },
    menuPrice() {
      return this.data ? this.data.menuPrice : null
    },
    smenuCd() {
      return this.data ? this.data.smenuCd : null
    },
    /** 포장 조건은 **이 표에 포장 입력칸이 열린 플랫폼**의 것만 말한다 — 없는 플랫폼의 조건은 없는 걱정이다(§16.7) */
    pickupPendingNote() {
      const platforms = this.rows
        .filter((row) => row.channels[DELIVERY_MENU_CHANNEL.PICKUP] != null)
        .map((row) => row.platform)
      return deliveryChannelPendingNote(DELIVERY_MENU_CHANNEL.PICKUP, platforms)
    },
  },

  watch: {
    data: {
      immediate: true,
      handler() {
        this.resetDrafts()
      },
    },
  },

  methods: {
    platformName: deliveryMenuPlatformName,
    /** 샵 표기 규칙은 utils/deliveryMenuSync 한 곳이 소유한다 (§16.179) */
    shop: shopText,
    hasPickup: platformHasPickupChannel,

    money(value) {
      return value == null ? '-' : `${util.formatMoney(Number(value))}원`
    },

    /**
     * 응답 → 편집 상태. 손댄 표시는 여기서 초기화된다 — 저장 뒤 응답이 다시 이 자리를 지난다.
     *
     * **`null` 을 `undefined` 로 바꿔 넣는다.** `el-input-number` 의 범위 보정이 `newVal <= min` 으로
     * 판정하는데 `null <= 0` 이 참이라 "오버라이드 없음"이 **`0` 원으로 보인다** — 사장님이 정하지도
     * 않은 목표가 화면에 서는 것이고, 실제로 그렇게 떴다. 어휘(`null` = 없음)를 위젯 사정에 맞춰
     * 바꾸는 대신 그 경계에서만 번역한다.
     */
    resetDrafts() {
      const next = {}
      for (const row of this.rows) {
        next[row.platform] = {}
        for (const channel of Object.keys(row.channels)) {
          const fold = row.channels[channel]
          if (!fold) continue
          next[row.platform][channel] = { value: fold.value == null ? undefined : fold.value, touched: false }
        }
      }
      this.drafts = next
    },

    /**
     * **값이 바뀐 순간만 선언이 된다** — `change` 는 값이 실제로 달라질 때만 온다.
     *
     * 비운 것도 선언이다("메뉴 단가를 따른다"). 그래서 값이 아니라 *만졌는가*를 기록한다 — 갈려 있던
     * 두 링크를 비워서 되돌리는 조작은 값만 보면 "안 바뀜"과 구분되지 않는다.
     */
    touch(platform, channel) {
      const slot = this.drafts[platform] && this.drafts[platform][channel]
      if (slot) slot.touched = true
    },

    hasChanges() {
      return channelPriceTargets(this.drafts).length > 0
    },

    /**
     * 손댄 축을 저장한다. **부모의 메뉴 저장이 성공한 뒤에** 부른다 (§16.177) — 순서가 반대면 방금
     * 정한 목표가 메뉴 가격 편집의 선언(`MENU_ROW`)에 정리되어 사라진다.
     *
     * @returns `{ok, saved, response}` — `ok=false` 의 문장은 인터셉터가 이미 띄웠다(서버가 만든
     *   그 문장이 다음 행동을 담는다). 부모는 인터셉터가 알 수 없는 사실 — 메뉴는 저장됐다는 것 — 만
     *   덧붙인다.
     */
    async save() {
      const targets = channelPriceTargets(this.drafts)
      // 빈 `targets` 는 서버가 400 이다 — 바뀐 것이 없으면 요청 자체를 만들지 않는다
      if (targets.length === 0 || this.smenuCd == null) return { ok: true, saved: false, response: null }
      try {
        const res = await this.$axios.put(`${DELIVERY_MENU_API}/menus/${this.smenuCd}/channel-prices`, { targets })
        // 서버가 접은 결과가 정본이다 — 부모가 이 응답을 다시 내려주면 편집 상태가 그것으로 맞춰진다
        this.$emit('saved', res.data)
        return { ok: true, saved: true, response: res.data }
      } catch (e) {
        return { ok: false, saved: false, response: null }
      }
    },
  },
}
</script>

<style scoped>
.dm-cp__alert {
  margin-bottom: 10px;
}
.dm-cp__base {
  margin-top: 4px;
}
.dm-cp__link {
  line-height: 1.7;
}
.dm-cp__effective {
  margin-left: 8px;
  color: #409eff;
}
.dm-cp__weak {
  color: #909399;
}
.dm-cp__input {
  width: 100%;
}
.dm-cp__mixed {
  margin-top: 4px;
  font-size: 12px;
  color: #e6a23c;
}
.dm-cp__foot {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
}
</style>
